import { Box, MenuList, Popover } from '@mui/material';

import { TP_OPTIONS_MENU_ID } from '../constants';
import { useTpOptionsMenu } from '../hooks';
import { TP_OPTIONS_MENU_LIST_SX, TP_OPTIONS_MENU_PAPER_SX } from '../styles';
import TpOptionsButton from './TpOptionsButton';
import TpOptionsMenuDivider from './TpOptionsMenuDivider';
import TpOptionsMenuRow from './TpOptionsMenuRow';
import TpOptionsSubmenu from './TpOptionsSubmenu';

import type { MouseEvent } from 'react';
import type { Bays } from '../../../../../utils/types';
import type { TpOptionsMenuItemId } from '../types';
import type { TpOptionsMenuProps } from './types';

export default function TpOptionsMenu({
  entries,
  disabled,
  treatmentPlan,
  treatmentPlans,
  activeTreatmentPlan,
  tpStatusIdByLabel,
  inProgressTpId,
  onItemClick,
  onBayPick,
  onPlanPick,
}: TpOptionsMenuProps) {
  const menu = useTpOptionsMenu();
  const isOpen = Boolean(menu.anchorEl);

  const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) =>
    menu.openMenu(event.currentTarget);

  const handleRowAction = (id: TpOptionsMenuItemId) => {
    onItemClick[id]?.();
    menu.closeMenu();
  };

  const handleBayPick = (bay: Bays) => {
    if (menu.submenu) onBayPick(menu.submenu.id, bay);
    menu.closeMenu();
  };

  const handlePlanPick = (planId: number) => {
    onPlanPick(planId);
    menu.closeMenu();
  };

  return (
    <Box data-testid="tpOperationDropDown" sx={{ gridColumn: 4 }}>
      <TpOptionsButton disabled={disabled} isOpen={isOpen} onClick={handleButtonClick} />
      <Popover
        id={TP_OPTIONS_MENU_ID}
        open={isOpen}
        anchorEl={menu.anchorEl}
        onClose={menu.closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: TP_OPTIONS_MENU_PAPER_SX, onMouseLeave: menu.closeSubmenu } }}
      >
        <MenuList
          autoFocusItem
          variant="menu"
          disablePadding
          sx={TP_OPTIONS_MENU_LIST_SX}
          onKeyDown={menu.handleListKeyDown}
        >
          {entries.map((entry) =>
            entry.type === 'divider' ? (
              <TpOptionsMenuDivider key={entry.id} />
            ) : (
              <TpOptionsMenuRow
                key={entry.id}
                entry={entry}
                isSubmenuOpen={
                  entry.submenuId !== undefined && menu.submenu?.id === entry.submenuId
                }
                onOpenSubmenu={menu.openSubmenu}
                onCloseSubmenu={menu.closeSubmenu}
                onAction={handleRowAction}
                onKeyDown={menu.handleRowKeyDown}
              />
            ),
          )}
        </MenuList>
        {menu.submenu ? (
          <TpOptionsSubmenu
            submenu={menu.submenu}
            treatmentPlan={treatmentPlan}
            treatmentPlans={treatmentPlans}
            activeTreatmentPlan={activeTreatmentPlan}
            tpStatusIdByLabel={tpStatusIdByLabel}
            inProgressTpId={inProgressTpId}
            onBayPick={handleBayPick}
            onPlanPick={handlePlanPick}
            onKeyDown={menu.handleSubmenuKeyDown}
          />
        ) : null}
      </Popover>
    </Box>
  );
}
